import { Divider } from '@aon/aon-tws-common-ui';
import * as React from 'react';
import { ReactReduxContext } from 'react-redux';
import Constants from '../../constants';
import { CustomQuestionsModalContainer } from '../../containers/questions/custom-questions/CustomQuestionsModalContainer';
import { QuestionsModalContainer } from '../../containers/questions/QuestionsModalContainer';
import { PreviousStepButton, SaveAndNextButton, SaveButton, SaveButtonsGroup } from '../../containers/save-buttons/SaveButtonsGroup';
import ContentConstants from '../../contentConstants';
import {
	IClientParticipantActionCreators,
	ICustomQuestionsModalActionCreators,
	IQuestionModalActionCreators,
	ISurveyActionCreators,
	ISurveyBuilderActionCreators,
	ISurveyQuestionEditModel,
	SurveyPropertyUnderValidation,
} from '../../models';
import { ISurveyBuilderState } from '../../store/SurveyBuilderStore';
import { ISurveyState } from '../../store/SurveyStore';
import { ValidationCollectionHelper } from '../../utilities';
import { CardLayout } from '../common';
import { SurveyNoteHeaderContainer } from '../notes/SurveyNoteHeaderContainer';
import { QuestionDeletionModal } from '../questions/QuestionDeletionModal';
import { QuestionSection } from './define-survey-content';

export type IDefineSurveyQuestionsProps = {
	match: { params: { surveyId: string } };
	isQuestionChangesLoading: boolean;
} & ISurveyBuilderState &
	ISurveyState &
	ISurveyBuilderActionCreators &
	ISurveyActionCreators &
	IQuestionModalActionCreators &
	ICustomQuestionsModalActionCreators &
	IClientParticipantActionCreators;

interface IState {
	showQuestionDeletionModal: boolean;
	questionToDelete?: ISurveyQuestionEditModel;
	dimensionsText: string;
}

export class DefineSurveyQuestions extends React.PureComponent<IDefineSurveyQuestionsProps, IState> {
	constructor(props: IDefineSurveyQuestionsProps) {
		super(props);
		this.state = { showQuestionDeletionModal: false, questionToDelete: undefined, dimensionsText: '' };
		this.ConfirmDeleteQuestion = this.ConfirmDeleteQuestion.bind(this);
		this.CloseDeleteQuestionModal = this.CloseDeleteQuestionModal.bind(this);
		this.PopupQuestionDeletionWarningModal = this.PopupQuestionDeletionWarningModal.bind(this);
	}

	public componentDidMount() {
		const paramsSurveyId = parseInt(this.props.match.params.surveyId, 10);
		this.props.GetConfiguredParticipantAttributes(paramsSurveyId);
	}

	public render() {
		const { surveyEditModel, validationResults } = this.props;
		const validator = new ValidationCollectionHelper(validationResults);

		return (
			<CardLayout
				loading={this.props.isLoading}
				title={ContentConstants.TITLE_DEFINE_SURVEY_CONTENT}
				header={<SurveyNoteHeaderContainer />}
			>
				{/* The multiple levels of the component structure (container -> component -> container -> component etc) 
					is not working when we have upgraded react-redux to latest version 7.2.1. 
					The connect function in the containers is not able to pass the existing redux context 
					from parent to child components through all levels. 
					So we are explicitly getting the store and passing to the container as prop.
					ToDo: We will need to explore if there is a different way to fix this issue*/}
				<ReactReduxContext.Consumer>
					{({ store }) => {
						return <QuestionsModalContainer store={store} />;
					}}
				</ReactReduxContext.Consumer>

				<CustomQuestionsModalContainer />
				<QuestionDeletionModal
					submitModal={this.ConfirmDeleteQuestion}
					closeModal={this.CloseDeleteQuestionModal}
					isOpen={this.state.showQuestionDeletionModal}
					dimensionsText={this.state.dimensionsText}
				></QuestionDeletionModal>
				<QuestionSection
					isQuestionChangesLoading={this.props.isQuestionChangesLoading}
					questions={surveyEditModel.questions}
					validationResult={validator.GetValidationResultForProperty(SurveyPropertyUnderValidation.questions)}
					openQuestionModal={this.props.OpenSurveyQuestionsModal}
					openCustomModal={this.props.OpenCustomQuestionsModal}
					openDemographicQuestionModal={this.props.OpenDemographicQuestionsModal}
					openCustomDemographicQuestionsModal={this.props.OpenCustomDemographicsQuestionModal}
					deleteQuestion={this.PopupQuestionDeletionWarningModal}
					UpdateAllQuestionTypes={this.props.UpdateAllQuestionTypes}
					ToggleAllQuestionsAreRequired={this.props.ToggleAllQuestionsAreRequired}
					supportedCultures={this.props.surveyEditModel.supportedCultures}
					ToggleQuestionIsRequired={this.props.ToggleQuestionIsRequired}
					selectionThreshold={Constants.QUESTION_LIMIT}
					sectionHeaderText={ContentConstants.SECTIONHEADERS_SURVEY_QUESTIONS}
				/>
				<Divider type='dashed' />
				<SaveButtonsGroup>
					<PreviousStepButton />
					<SaveButton />
					<SaveAndNextButton />
				</SaveButtonsGroup>
			</CardLayout>
		);
	}

	private PopupQuestionDeletionWarningModal(question: ISurveyQuestionEditModel) {
		if (
			question.dimensionNames &&
			question.dimensionNames.length > 0 &&
			question.dimensionNames.some(
				(d) =>
					this.props.surveyEditModel.questions.some(
						(q) => q.parentQuestionId != null && q.responseOptions && q.responseOptions.some((o) => o === d),
					) && this.props.surveyEditModel.questions.filter((q) => q.dimensionNames && q.dimensionNames.indexOf(d) >= 0).length === 1,
			)
		) {
			const relatedDimensions: string[] = question.dimensionNames.filter((d) =>
				this.props.surveyEditModel.questions.some(
					(q) => q.parentQuestionId != null && q.responseOptions && q.responseOptions.indexOf(d) >= 0,
				),
			);
			this.setState((state) => {
				return { ...state, showQuestionDeletionModal: true, questionToDelete: question, dimensionsText: relatedDimensions.join(', ') };
			});
		} else {
			this.ConfirmDeleteQuestion(question);
		}
	}

	private ConfirmDeleteQuestion(question: ISurveyQuestionEditModel | null) {
		this.setState((state) => {
			return { ...state, showQuestionDeletionModal: false };
		});
		if (question) {
			this.props.DeleteSurveyQuestion(question);
		} else if (this.state.questionToDelete) {
			this.props.DeleteSurveyQuestion(this.state.questionToDelete);
		}
	}

	private CloseDeleteQuestionModal() {
		this.setState((state) => {
			return { ...state, showQuestionDeletionModal: false };
		});
	}
}
